import { useTranslation } from 'react-i18next';
import { Clapperboard } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { formatVideoDuration } from '@/lib/video-metadata';
import type { FrameioShareVideos } from '@/queries/frameio';
import type { ShareImportPlatform } from '@/lib/shareImport/shareImport';

interface FrameioVideoPickerProps {
  share: FrameioShareVideos;
  /** i18n namespace for provider copy (Frame.io and MediaSilo share this picker). */
  copyNamespace?: ShareImportPlatform;
  selectedIds: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
  maxDurationSeconds: number;
  disabled?: boolean;
}

export function FrameioVideoPicker({
  share, selectedIds, onChange, maxDurationSeconds, disabled = false, copyNamespace = 'frameio',
}: FrameioVideoPickerProps) {
  const { t } = useTranslation();
  const selectable = share.videos.filter((video) => video.duration_seconds <= maxDurationSeconds);
  const allSelected = selectable.length > 0 && selectable.every((video) => selectedIds.has(video.asset_id));

  const toggle = (assetId: string) => {
    const next = new Set(selectedIds);
    if (next.has(assetId)) next.delete(assetId);
    else next.add(assetId);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Clapperboard className="h-4 w-4 flex-shrink-0 text-primary" aria-hidden="true" />
          <span className="truncate text-sm font-semibold">{share.share_name || t(`${copyNamespace}.shareTitle`)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t(`${copyNamespace}.selectedCount`, { selected: selectedIds.size, total: share.videos.length })}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="frameio-select-all"
            disabled={disabled || selectable.length === 0}
            onClick={() => onChange(allSelected ? new Set() : new Set(selectable.map((video) => video.asset_id)))}
          >
            {allSelected ? t(`${copyNamespace}.clearSelection`) : t(`${copyNamespace}.selectAll`)}
          </Button>
        </div>
      </div>

      <ul className="max-h-72 space-y-1 overflow-y-auto rounded-lg border border-border/60 p-1">
        {share.videos.map((video) => {
          const tooLong = video.duration_seconds > maxDurationSeconds;
          const checkboxId = `frameio-video-${video.asset_id}`;
          const orientation = video.width && video.height
            ? (video.height >= video.width ? t(`${copyNamespace}.portrait`) : t(`${copyNamespace}.landscape`))
            : null;
          return (
            <li key={video.asset_id}>
              <label
                htmlFor={checkboxId}
                className="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-muted/40 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60"
              >
                <Checkbox
                  id={checkboxId}
                  data-testid={checkboxId}
                  checked={selectedIds.has(video.asset_id)}
                  disabled={disabled || tooLong}
                  onCheckedChange={() => toggle(video.asset_id)}
                />
                {video.thumbnail_url ? (
                  <img
                    src={video.thumbnail_url}
                    alt=""
                    loading="lazy"
                    className="h-10 w-10 flex-shrink-0 rounded object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 flex-shrink-0 rounded bg-muted" aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">{video.name}</span>
                  {tooLong && <span className="block text-xs text-muted-foreground">{t(`${copyNamespace}.tooLong`)}</span>}
                </span>
                {orientation && <Badge variant="outline" className="text-xs">{orientation}</Badge>}
                <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">
                  {formatVideoDuration(video.duration_seconds)}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {share.truncated && <p className="text-xs text-muted-foreground">{t(`${copyNamespace}.truncated`)}</p>}
    </div>
  );
}
